import { useMemo, useState } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import { Badge, Button, Checkbox } from '../ui';

const MOSTRATI = 200;

/**
 * Scelta di persone da elenchi lunghi: ricerca, filtro per categoria,
 * "aggiungi tutti i filtrati", chip removibili e contatore sul limite.
 * Con molte persone si rende solo la prima finestra, non l'elenco intero.
 */
export default function PersonPicker({ people, selected, onChange, max = 50, label = 'Membri', hint }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  const categorie = useMemo(
    () => [...new Set(people.map(p => p.category).filter(Boolean))].sort(),
    [people]);

  const filtrate = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return people.filter(p =>
      (!category || p.category === category) &&
      (!q || `${p.display_name} ${p.short_name || ''} ${p.category || ''}`.toLocaleLowerCase().includes(q)));
  }, [people, query, category]);

  const scelte = new Set(selected);
  const daAggiungere = filtrate.filter(p => !scelte.has(p.id));
  const pieno = selected.length >= max;

  const commuta = (id, acceso) =>
    onChange(acceso ? [...selected, id] : selected.filter(x => x !== id));

  const aggiungiTutti = () =>
    onChange([...selected, ...daAggiungere.slice(0, max - selected.length).map(p => p.id)]);

  return (
    <fieldset className="rounded-lg border border-line p-3">
      <legend className="px-1 text-sm font-medium text-ink">{label}</legend>
      {hint && <p className="mb-2 text-xs text-muted">{hint}</p>}

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <MdSearch size={18} aria-hidden className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
          <input aria-label={`Cerca fra ${label.toLowerCase()}`} placeholder="Cerca per nome"
            value={query} onChange={e => setQuery(e.target.value)}
            className="w-full rounded border border-line bg-surface py-2 pl-8 pr-3 text-base md:text-sm" />
        </div>
        {categorie.length > 0 && (
          <select aria-label="Filtra per categoria" value={category} onChange={e => setCategory(e.target.value)}
            className="rounded border border-line bg-surface px-2 py-2 text-sm">
            <option value="">Tutte le categorie</option>
            {categorie.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <Button size="sm" onClick={aggiungiTutti} disabled={!daAggiungere.length || pieno}>
          Aggiungi tutti i filtrati ({daAggiungere.length})
        </Button>
      </div>

      <div className="my-2 flex flex-wrap gap-1" aria-live="polite">
        {selected.map(id => {
          const persona = people.find(p => p.id === id);
          return (
            <button key={id} type="button" onClick={() => commuta(id, false)}
              className="inline-flex items-center gap-1 rounded-full border border-brand-500/30 bg-brand-50 px-2 py-1 text-2xs text-brand-700">
              {persona ? (persona.short_name || persona.display_name) : 'Persona non più disponibile'}
              <MdClose size={12} aria-hidden />
              <span className="sr-only">Togli dalla selezione</span>
            </button>
          );
        })}
      </div>

      <p className={`mb-2 text-xs ${pieno ? 'text-warning' : 'text-muted'}`}>
        {selected.length} di {max} persone{pieno && ' — limite raggiunto'}
      </p>

      <div className="max-h-56 overflow-auto rounded border border-line">
        {filtrate.slice(0, MOSTRATI).map(p => (
          <div key={p.id} className="border-b border-line px-2 py-1.5 last:border-0">
            <Checkbox label={
              <span className="flex flex-wrap items-center gap-2">
                {p.short_name || p.display_name}
                {p.category && <Badge>{p.category}</Badge>}
                {!p.identities?.length && <Badge tone="warning">Nessuna identità</Badge>}
              </span>}
              checked={scelte.has(p.id)} disabled={!scelte.has(p.id) && pieno}
              onChange={e => commuta(p.id, e.target.checked)} />
          </div>
        ))}
        {!filtrate.length && <p className="p-3 text-sm text-muted">Nessuna persona corrisponde ai filtri.</p>}
        {filtrate.length > MOSTRATI && (
          <p className="p-2 text-xs text-muted">Mostrate {MOSTRATI} di {filtrate.length}: restringi con la ricerca.</p>
        )}
      </div>
    </fieldset>
  );
}
